import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type React from 'react'
import { type MutableRefObject, useState } from 'react'

import { OrderFreightDrawer } from '../OrderFreightDetails/components/OrderFreightDrawer'
import { orderColumnDefs } from './components/columnDefs/byOrderColumnDefs'

import type { TransactionDetail, TransactionRow } from '../../../api/useFreight/types'
import type { GridApi, ICellRendererParams, IFilter, RowHeightParams } from 'ag-grid-community'
import { byOrderLineItemColumns } from './components/columnDefs'
import { useSecurityContext } from '../../Navigation/SecurityContext'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'

const STORAGE_KEY = 'freight-line-items-by-order'

export interface ByOrderViewProps {
  formatCurrency: (value: number, useSymbol: boolean, precision?: number) => string
  data: TransactionRow[]
  isLoading: boolean
  actionButtons: React.ReactNode
  // @ts-ignore
  serverParams: Record<string, unknown>
  setServerFilters: (filters: Record<string, unknown>) => void
  // @ts-ignore
  filters: Record<string, unknown>
  gridAPIRef: MutableRefObject<GridApi>
  costRevenue: 'Cost' | 'Revenue'
}

export function ByOrderView({
  costRevenue,
  gridAPIRef,
  formatCurrency,
  data: orderData,
  isLoading,
  actionButtons,
  serverParams,
  setServerFilters,
  filters,
}: ByOrderViewProps) {
  const { scopes } = useSecurityContext()
  const { updateSetting } = useUserSettings()
  // State management
  const [drawerState, setDrawerState] = useState<{
    visible: boolean
    orderId?: string
    currentIndex: number
  }>({
    visible: false,
    currentIndex: -1,
  })

  const handleNavigate = (newIndex: number) => {
    const order = orderData[newIndex]
    if (order) {
      setDrawerState({
        visible: true,
        orderId: order.order_id,
        currentIndex: newIndex,
      })
    }
  }

  const selectedOrder = orderData.find((order) => order.order_id === drawerState.orderId)

  return (
    <>
      <GraviGrid
        controlBarProps={{
          title: 'Freight Line Items',
          actionButtons,
          // @ts-ignore
          serverParams,
          setFilters: setServerFilters,
          // @ts-ignore
          filters,
        }}
        rowData={orderData}
        loading={isLoading}
        externalRef={gridAPIRef}
        storageKey={STORAGE_KEY}
        agPropOverrides={{
          suppressRowTransform: true,
          rowSelection: 'multiple',
          enableRangeSelection: true,
          masterDetail: true,
          detailRowAutoHeight: true,
          detailCellRenderer: ByOrderLineItemsGrid,
          columnDefs: orderColumnDefs,
          detailCellRendererParams: {
            context: {
              scopes,
              formatCurrency,
              setDrawerState: (state: typeof drawerState) => {
                setDrawerState({
                  ...state,
                  currentIndex: orderData.findIndex((order) => order.order_id === state.orderId),
                })
              },
            },
            style: { padding: '8px 16px' },
          },
          context: {
            scopes,
            setDrawerState: (state: typeof drawerState) => {
              setDrawerState({
                ...state,
                currentIndex: orderData.findIndex((order) => order.order_id === state.orderId),
              })
            },
          },
          getRowId: (params) => params.data.order_id + params.data.status + (params.data.customer_id || ''),
          animateRows: true,
          suppressMaintainUnsortedOrder: false,
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />

      <OrderFreightDrawer
        open={drawerState.visible}
        onClose={() => setDrawerState({ visible: false, currentIndex: -1 })}
        order={selectedOrder}
        allOrders={orderData}
        currentIndex={drawerState.currentIndex}
        onNavigate={handleNavigate}
        costRevenue={costRevenue}
      />
    </>
  )
}

interface DetailGridProps {
  data: TransactionRow
}

// height calculation function for the grid
const getGridHeight = (details: TransactionDetail[]) => {
  let totalHeight = 0
  for (const detail of details) {
    totalHeight += getRowHeight(detail)
  }
  return totalHeight + 40
}

const overrideCols = [
  'amount_override',
  'total_override',
  'product_name_override',
  'product_group_override',
  'origin_name_override',
  'destination_name_override',
  'bol_number_override',
  'rate_override',
  'uom_override',
  'subtype_override',
]

const getRowHeight = (data: TransactionDetail | undefined) => {
  let height = 30
  if (!data) return height
  overrideCols.forEach((col) => {
    if (data[col as keyof TransactionDetail] !== null && data[col as keyof TransactionDetail] !== undefined) {
      height = 40
    }
  })
  return height
}

// DetailGrid component
export function ByOrderLineItemsGrid(props: DetailGridProps) {
  const { data: detailRowData } = props
  const rawDetailData = detailRowData.details || []
  const hasBothCostAndRevenue =
    rawDetailData.some((detail) => detail.book_type === 'Cost') &&
    rawDetailData.some((detail) => detail.book_type === 'Revenue')

  const { scopes } = useSecurityContext()
  const isCreditRebillMode = detailRowData.type === 'credit' || detailRowData.type === 'rebill'
  return (
    <Vertical style={{ height: getGridHeight(rawDetailData) }}>
      <GraviGrid<TransactionDetail, IFilter>
        primaryKey='id'
        rowData={rawDetailData}
        sideBar={undefined}
        storageKey='freight-line-items-detail-expand'
        // @ts-ignore had to do this to get the downloadbuttons to hide the columns.
        columnDefs={byOrderLineItemColumns(isCreditRebillMode).filter(
          (col) => (hasBothCostAndRevenue && col.field !== 'counterparty') || col.field !== 'error'
        )}
        context={{
          scopes,
        }}
        agPropOverrides={{
          context: {
            scopes,
          },
          headerHeight: 32,
          scrollbarWidth: 0,
          pagination: false,
          suppressPaginationPanel: true,
          rowGroupPanelShow: 'onlyWhenGrouping',
          getRowHeight: ({ data }: RowHeightParams) => getRowHeight(data),
          getRowId: (params) => params.data.id,

          animateRows: true,
        }}
      />
    </Vertical>
  )
}
